import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { useLocation } from "wouter";
import { AlertTriangle, ArrowRight, Check, CheckCircle2, Crown, Loader2, Shield, Users } from "lucide-react";
import { Link } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import MembershipSignInDialog from "@/components/MembershipSignInDialog";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { chiNotify } from "@/components/ChiNotification";
import { trpc } from "@/lib/trpc";
import { getMembershipCheckoutStartError, POPUP_BLOCKED_CHECKOUT_ERROR, type MembershipCheckoutStartError } from "@/lib/membershipCheckoutFeedback";
import { MEMBERSHIP_JOIN_CTA, MEMBERSHIP_PAID_TIERS_ENABLED } from "@shared/membershipPaidTiers";

/**
 * The free column states what a non-member can actually do today, so it has to
 * stay honest — every row here is a promise the product has to keep.
 */
const FEATURES = [
  { label: "Register for an event (roundtable, signup, request to join)", free: true, premium: true },
  { label: "List of speakers", free: true, premium: true },
  { label: "Browse the Member Directory", free: true, premium: true },
  { label: "Your name and photo in the directory", free: true, premium: true },
  { label: "Your title, company and location shown", free: false, premium: true },
  { label: "Your industry and interests shown", free: false, premium: true },
  { label: "Your full bio shown", free: false, premium: true },
  { label: "Your verified profile links shown", free: false, premium: true },
  { label: "Available-to-contract listing", free: false, premium: true },
  { label: "Priority event registration", free: false, premium: true },
  { label: "Member badge on your profile", free: false, premium: true },
];

const FREE_FEATURES = FEATURES.filter(feature => feature.free);
const MEMBER_ONLY_COUNT = FEATURES.length - FREE_FEATURES.length;

/**
 * The page offers two account types, not one product with an upsell attached.
 * Both are real: /create-account provisions a usable profile without payment,
 * so the free tab has to describe an account somebody actually gets.
 */
type PlanId = "free" | "member";

const PLAN_ORDER: PlanId[] = ["free", "member"];

const PLANS: Record<PlanId, {
  tabLabel: string;
  name: string;
  cadence: string;
  price: string;
  accent: string;
  benefitsLabel: string;
}> = {
  free: {
    tabLabel: "Free",
    name: "Free Account",
    cadence: "Always free · No card required",
    price: "$0.00",
    accent: "#22C55E",
    benefitsLabel: "Free account",
  },
  member: {
    tabLabel: "Member · Yearly",
    name: "CHI Alliance Membership",
    cadence: "Annual plan · Renews annually",
    price: "$199.00",
    accent: "#F9A8D4",
    benefitsLabel: "Membership",
  },
};

/**
 * Segmented control for the account type. Rendered inside the purchase card but
 * owned by the page, because it also drives which benefits the left panel lists.
 */
function PlanTabs({ plan, onChange }: { plan: PlanId; onChange: (next: PlanId) => void }) {
  const tabRefs = useRef<Partial<Record<PlanId, HTMLButtonElement | null>>>({});

  const moveSelection = (event: KeyboardEvent<HTMLButtonElement>) => {
    const { key } = event;
    if (key !== "ArrowLeft" && key !== "ArrowRight" && key !== "Home" && key !== "End") return;
    event.preventDefault();
    const current = PLAN_ORDER.indexOf(plan);
    const nextIndex = key === "Home"
      ? 0
      : key === "End"
        ? PLAN_ORDER.length - 1
        : (current + (key === "ArrowRight" ? 1 : -1) + PLAN_ORDER.length) % PLAN_ORDER.length;
    const next = PLAN_ORDER[nextIndex];
    onChange(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <div role="tablist" aria-label="Account type" className="membership-plan-tabs">
      {PLAN_ORDER.map(id => (
        <button
          key={id}
          ref={node => { tabRefs.current[id] = node; }}
          type="button"
          role="tab"
          id={`membership-plan-tab-${id}`}
          aria-selected={plan === id}
          aria-controls="membership-plan-panel"
          tabIndex={plan === id ? 0 : -1}
          onClick={() => onChange(id)}
          onKeyDown={moveSelection}
          className="membership-plan-tabs__tab"
          data-plan={id}
        >
          {PLANS[id].tabLabel}
        </button>
      ))}
    </div>
  );
}

/**
 * Lists only what the selected account type includes.
 *
 * The free list is short by design — it is four rows, and showing it alone would
 * leave the $199 tier unargued, so the free view ends with a count of what
 * Membership adds and a control back to that tab.
 */
function MembershipBenefits({ plan, onChange }: { plan: PlanId; onChange: (next: PlanId) => void }) {
  const rows = plan === "free" ? FREE_FEATURES : FEATURES;
  const { accent, benefitsLabel } = PLANS[plan];

  return (
    <section
      className="membership-benefits chi-showcase-panel"
      role="tabpanel"
      id="membership-plan-panel"
      aria-labelledby={`membership-plan-tab-${plan}`}
      tabIndex={-1}
    >
      <div className="membership-benefits__header">
        <span className="membership-benefits__title">What&rsquo;s included</span>
        <span className="membership-benefits__badge" style={{ color: accent, borderColor: `${accent}55`, background: `${accent}14` }}>{benefitsLabel}</span>
      </div>
      <ul className="membership-benefits__list">
        {rows.map(feature => (
          <li key={feature.label} className="membership-benefits__row">
            <Check size={17} strokeWidth={2.5} style={{ color: accent, flexShrink: 0 }} aria-hidden="true" />
            <span>{feature.label}</span>
          </li>
        ))}
      </ul>
      {plan === "free" && (
        <button type="button" onClick={() => onChange("member")} className="membership-benefits__upsell">
          <span>{MEMBER_ONLY_COUNT} more benefits with Membership</span>
          <ArrowRight size={15} aria-hidden="true" />
        </button>
      )}
    </section>
  );
}

function EntitlementNotice({ checkoutState }: { checkoutState: string | null }) {
  const { isLoggedIn, isPremium, upgradeToPremium } = useAuth();
  // Membership is a public landing page. Entitlement is a protected query, and
  // an unauthorized response trips the global redirect in main.tsx — so it must
  // only run for a signed-in visitor, never on an anonymous page view.
  const entitlementQuery = trpc.members.myEntitlement.useQuery(undefined, { retry: false, enabled: isLoggedIn });

  const refreshEntitlement = entitlementQuery.refetch;

  useEffect(() => {
    if (checkoutState === "success") {
      upgradeToPremium();
      void refreshEntitlement();
    }
  }, [checkoutState, refreshEntitlement, upgradeToPremium]);

  if (checkoutState === "cancelled") {
    return (
      <div role="status" aria-live="polite" style={{ marginBottom: "1.5rem", padding: "0.875rem 1rem", display: "flex", gap: ".65rem", alignItems: "flex-start", background: "rgba(245,166,35,.08)", border: "1px solid rgba(245,166,35,.25)", borderRadius: ".75rem" }}>
        <AlertTriangle size={17} style={{ color: "#F5A623", flexShrink: 0, marginTop: 2 }} />
        <p style={{ color: "#FDE68A", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5, margin: 0 }}><strong>Checkout was cancelled.</strong> No charge was made. Your account remains available, and you can return to secure checkout whenever you are ready.</p>
      </div>
    );
  }

  if (checkoutState === "success" && !isPremium) {
    return (
      <div role="status" aria-live="polite" style={{ marginBottom: "1.5rem", padding: "0.875rem 1rem", display: "flex", gap: ".65rem", alignItems: "flex-start", background: "rgba(0,212,255,.07)", border: "1px solid rgba(0,212,255,.22)", borderRadius: ".75rem" }}>
        <Loader2 size={17} className="animate-spin" style={{ color: "#67E8F9", flexShrink: 0, marginTop: 2 }} />
        <div>
          <p style={{ color: "#CFFAFE", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5, margin: 0 }}><strong>Payment return received — confirming your membership.</strong> We will activate access only after Stripe’s signed confirmation is processed.</p>
          <button type="button" onClick={() => { upgradeToPremium(); void refreshEntitlement(); }} disabled={entitlementQuery.isFetching} style={{ marginTop: ".6rem", padding: ".38rem .6rem", border: "1px solid rgba(103,232,249,.35)", borderRadius: ".45rem", background: "rgba(103,232,249,.08)", color: "#CFFAFE", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".7rem", fontWeight: 700, cursor: entitlementQuery.isFetching ? "wait" : "pointer" }}>{entitlementQuery.isFetching ? "Checking status…" : "Check membership status"}</button>
        </div>
      </div>
    );
  }

  if (checkoutState === "success" && isPremium) {
    return (
      <div role="status" aria-live="polite" style={{ marginBottom: "1.5rem", padding: "0.875rem 1rem", display: "flex", gap: ".65rem", alignItems: "flex-start", background: "rgba(34,197,94,.08)", border: "1px solid rgba(34,197,94,.26)", borderRadius: ".75rem" }}>
        <CheckCircle2 size={17} style={{ color: "#86EFAC", flexShrink: 0, marginTop: 2 }} />
        <p style={{ color: "#DCFCE7", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5, margin: 0 }}><strong>Membership confirmed.</strong> Stripe’s verified confirmation has activated your member access.</p>
      </div>
    );
  }

  return null;
}

function MembershipCheckoutButton({ autoStart = false }: { autoStart?: boolean }) {
  const checkout = trpc.members.startAnnualCheckout.useMutation();
  const pendingWindow = useRef<Window | null>(null);
  const [checkoutStatus, setCheckoutStatus] = useState<"idle" | "preparing" | "opened" | "error">("idle");
  const [checkoutError, setCheckoutError] = useState<MembershipCheckoutStartError | null>(null);

  const beginCheckout = () => {
    setCheckoutStatus("preparing");
    setCheckoutError(null);

    const checkoutWindow = window.open("about:blank", "_blank");
    if (!checkoutWindow) {
      setCheckoutStatus("error");
      setCheckoutError(POPUP_BLOCKED_CHECKOUT_ERROR);
      chiNotify.error(POPUP_BLOCKED_CHECKOUT_ERROR.title, { description: POPUP_BLOCKED_CHECKOUT_ERROR.description });
      return;
    }
    checkoutWindow.opener = null;
    pendingWindow.current = checkoutWindow;

    checkout.mutate(undefined, {
      onSuccess: ({ checkoutUrl }) => {
        const secureCheckoutWindow = pendingWindow.current;
        if (!secureCheckoutWindow || secureCheckoutWindow.closed) {
          setCheckoutStatus("error");
          const error = { title: "The checkout tab was closed", description: "No charge was made. Choose secure checkout again to open a new Stripe tab." };
          setCheckoutError(error);
          chiNotify.error(error.title, { description: error.description });
          return;
        }
        secureCheckoutWindow.location.assign(checkoutUrl);
        setCheckoutStatus("opened");
        chiNotify.info("Opening secure Stripe checkout", { description: "Complete your annual membership in the new tab, then return here." });
      },
      onError: error => {
        pendingWindow.current?.close();
        const checkoutStartError = getMembershipCheckoutStartError(error.message);
        setCheckoutStatus("error");
        setCheckoutError(checkoutStartError);
        chiNotify.error(checkoutStartError.title, { description: checkoutStartError.description });
      },
      onSettled: () => {
        pendingWindow.current = null;
      },
    });
  };

  /*
   * Continues automatically when the visitor has just signed in from the modal.
   * They already pressed the checkout button once; making them press it again
   * after authenticating is the detour the modal exists to avoid.
   *
   * Guarded by a ref so a re-render cannot open a second Stripe tab.
   */
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!autoStart || autoStarted.current) return;
    autoStarted.current = true;
    beginCheckout();
    // beginCheckout is stable for this component's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  return (
    <>
      <button
        type="button"
        onClick={beginCheckout}
        disabled={checkout.isPending}
        className="chi-btn-primary membership-checkout__button"
      >
        {checkout.isPending ? <><Loader2 size={17} className="animate-spin" /> Preparing secure checkout…</> : checkoutStatus === "opened" ? <><Crown size={17} /> Open secure checkout again</> : <><Crown size={17} /> Continue to Secure Checkout — $199/year</>}
      </button>
      {checkoutStatus === "preparing" && <div role="status" aria-live="polite" style={{ marginTop: ".75rem", padding: ".7rem .8rem", borderRadius: ".6rem", background: "rgba(0,212,255,.07)", border: "1px solid rgba(0,212,255,.22)", color: "#CFFAFE", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>Preparing a secure Stripe checkout window. Please keep this page open.</div>}
      {checkoutStatus === "opened" && <div role="status" aria-live="polite" style={{ marginTop: ".75rem", padding: ".7rem .8rem", borderRadius: ".6rem", background: "rgba(34,197,94,.07)", border: "1px solid rgba(34,197,94,.24)", color: "#DCFCE7", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}><strong>Secure checkout is open in a new tab.</strong> Complete payment there, then return to this page. Your access activates only after Stripe confirms the payment.</div>}
      {checkoutStatus === "error" && checkoutError && <div role="alert" style={{ marginTop: ".75rem", padding: ".7rem .8rem", borderRadius: ".6rem", background: "rgba(244,63,94,.09)", border: "1px solid rgba(244,63,94,.3)", color: "#FFE4E6", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}><strong>{checkoutError.title}</strong><br />{checkoutError.description}</div>}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: ".4rem", marginTop: ".875rem" }}>
        <Shield size={16} strokeWidth={2.25} style={{ color: "#8092AC", flexShrink: 0 }} />
        <span style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: ".72rem" }}>Payments are securely processed by Stripe. Your membership is activated after verified confirmation.</span>
      </div>
    </>
  );
}

/**
 * Signed-out entry to membership: sign in without leaving the page, or go and
 * create an account.
 *
 * Replaces guest-email checkout as the default. Buying with only an email meant
 * paying before the address was ever verified, so a typo produced a paid Stripe
 * subscription attached to a mailbox nobody could sign in to — recoverable only by
 * hand. Signing in first means the payer and the account are the same verified
 * person before money moves.
 *
 * The cost is honest: this is more steps than guest checkout, and some visitors
 * will leave at the modal who would have paid. GuestMembershipCheckout is kept
 * below rather than deleted so that trade can be reversed without rebuilding it.
 */
function MembershipAuthChoice({ plan }: { plan: PlanId }) {
  const [signInOpen, setSignInOpen] = useState(false);
  const [signedInNow, setSignedInNow] = useState(false);
  const [, navigate] = useLocation();

  // Once signed in, hand straight to the authenticated button with autoStart, so
  // the visitor lands in Stripe rather than back on a button they already pressed.
  // Only for the paid tier — a free-tier visitor who signs in already has the
  // account they came for, so sending them to Stripe would charge them for
  // something they did not ask to buy.
  if (signedInNow && plan === "member") return <MembershipCheckoutButton autoStart />;

  /*
   * Creating an account is the primary action, and signing in is the secondary one.
   *
   * It was the other way round, which meant a visitor with no account clicked the
   * prominent button, hit a sign-in modal, and — for those who did have an account
   * — went straight to Stripe. Either way the signup flow that collects a photo,
   * bio, and interests was reachable only through the quieter link, so members
   * arrived paid with an empty directory profile.
   */
  return (
    <>
      <div className="membership-auth-choice">
        <div className="chi-cta-pair membership-auth-choice__actions">
      <Link
        href={plan === "free" ? "/create-account?source=membership-free" : "/create-account?source=membership"}
        className="chi-btn-primary membership-checkout__button"
      >
        <Users size={20} strokeWidth={2.25} aria-hidden="true" /> Create Account
      </Link>
      <button
        type="button"
        onClick={() => setSignInOpen(true)}
        className="chi-btn-outline membership-checkout__button membership-checkout__button--secondary"
      >
        <Crown size={20} strokeWidth={2.25} aria-hidden="true" /> {plan === "free" ? "Sign in" : "Sign in & continue"}
      </button>
        </div>
        <p style={{ color: "#7F8BA0", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5, margin: ".65rem 0 0", textAlign: "center" }}>
          {plan === "free"
            ? "Create your profile in four short steps. No card required, and you can upgrade to Membership at any time."
            : "New here? Create your profile in four short steps, then continue to secure checkout."}
        </p>

      {plan === "member" && (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: ".4rem", marginTop: ".875rem" }}>
        <Shield size={16} strokeWidth={2.25} style={{ color: "#8092AC", flexShrink: 0 }} />
        <span style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: ".72rem" }}>Payments are securely processed by Stripe. Your membership is activated after verified confirmation.</span>
      </div>
      )}
      </div>

      <MembershipSignInDialog
        open={signInOpen}
        onOpenChange={setSignInOpen}
        onSignedIn={() => {
          setSignedInNow(true);
          // A free-tier visitor signing in has nothing left to buy here, so send
          // them to the directory their account gives them rather than leaving
          // them on a purchase card for a tier they did not choose.
          if (plan === "free") navigate("/member-directory");
        }}
      />
    </>
  );
}

/**
 * Guest checkout, retained but no longer the default path — see
 * MembershipAuthChoice above.
 *
 * Membership is a public offer, so a signed-out visitor buys with an email
 * address instead of being sent to /login first. The email is carried into the
 * Stripe form as the customer email, so it is only typed once, and the account
 * is provisioned server-side before the session is created so the verified
 * webhook always has a member to activate.
 */
function GuestMembershipCheckout() {
  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const checkout = trpc.members.startAnnualCheckoutForEmail.useMutation();
  const pendingWindow = useRef<Window | null>(null);

  const beginCheckout = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setFieldError("Enter your email address to continue.");
      return;
    }
    setFieldError(null);

    pendingWindow.current = window.open("about:blank", "_blank");
    if (pendingWindow.current) pendingWindow.current.opener = null;

    checkout.mutate(
      { email: trimmed },
      {
        onSuccess: ({ checkoutUrl }) => {
          const checkoutWindow = pendingWindow.current;
          if (checkoutWindow && !checkoutWindow.closed) {
            checkoutWindow.location.assign(checkoutUrl);
          } else {
            window.open(checkoutUrl, "_blank", "noopener,noreferrer");
          }
          chiNotify.info("Opening secure Stripe checkout", {
            description: "Complete your annual membership in the new tab, then return here.",
          });
        },
        onError: error => {
          pendingWindow.current?.close();
          setFieldError(error.message);
          chiNotify.error("Could not start secure checkout", { description: error.message });
        },
        onSettled: () => {
          pendingWindow.current = null;
        },
      },
    );
  };

  return (
    <form onSubmit={beginCheckout} noValidate style={{ display: "grid", gap: ".55rem" }}>
      <label htmlFor="membership-email" style={{ color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".72rem", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase" }}>
        Email address <span aria-hidden="true" style={{ color: "#F9A8D4" }}>*</span>
      </label>
      <input
        id="membership-email"
        name="email"
        type="email"
        required
        autoComplete="email"
        value={email}
        onChange={event => { setEmail(event.target.value); if (fieldError) setFieldError(null); }}
        placeholder="Enter your email address"
        aria-invalid={fieldError ? true : undefined}
        aria-describedby={fieldError ? "membership-email-error" : "membership-email-hint"}
        disabled={checkout.isPending}
        style={{ width: "100%", minHeight: 48, padding: ".8rem .9rem", color: "#FFF", background: "rgba(255,255,255,.045)", border: fieldError ? "1px solid rgba(244,63,94,.75)" : "1px solid rgba(255,255,255,.13)", borderRadius: ".65rem", outline: "none", fontFamily: "'Inter', sans-serif", fontSize: ".92rem" }}
      />
      {fieldError ? (
        <p id="membership-email-error" role="alert" style={{ margin: 0, color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>{fieldError}</p>
      ) : (
        <p id="membership-email-hint" style={{ margin: 0, color: "#7E8CA0", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>
          We carry this address into Stripe so you only enter it once, and use it to activate your membership.
        </p>
      )}
      <button
        type="submit"
        disabled={checkout.isPending}
        className="chi-btn-primary membership-checkout__button"
      >
        {checkout.isPending ? <><Loader2 size={17} className="animate-spin" /> Preparing checkout…</> : <><Crown size={17} /> Become a Member — $199/year</>}
      </button>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: ".4rem", marginTop: ".5rem" }}>
        <Shield size={16} strokeWidth={2.25} style={{ color: "#8092AC", flexShrink: 0 }} />
        <span style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: ".72rem" }}>Payments are securely processed by Stripe. Your membership is activated after verified confirmation.</span>
      </div>
    </form>
  );
}

function FreeMembershipJoin() {
  const { isLoggedIn } = useAuth();
  const [, navigate] = useLocation();
  const utils = trpc.useUtils();
  const activate = trpc.members.activateComplimentaryMembership.useMutation({
    onSuccess: async () => {
      await utils.members.myEntitlement.invalidate();
      await utils.auth.me.invalidate();
      navigate("/member-directory");
    },
  });

  return (
    <>
      <header className="membership-page__hero">
        <div style={{ display: "inline-flex", alignItems: "center", gap: ".5rem", padding: ".35rem 1rem", background: "rgba(233,30,140,.08)", border: "1px solid rgba(233,30,140,.2)", borderRadius: 999, marginBottom: "1.25rem" }}>
          <Users size={13} style={{ color: "#F9A8D4", flexShrink: 0 }} />
          <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: ".7rem", fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: "#F9A8D4" }}>Membership is free</span>
        </div>
        <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2.5rem, 7vw, 4.2rem)", color: "white", margin: "0 0 1rem", lineHeight: 1 }}>Join the CHI Alliance free</h1>
        <p style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body)", lineHeight: 1.7, margin: 0 }}>Create your profile and become an active member. No card, no checkout, and no membership fee.</p>
      </header>
      <div className="membership-page__layout">
        <section className="membership-benefits chi-showcase-panel">
          <div className="membership-benefits__header">
            <span className="membership-benefits__title">What&rsquo;s included</span>
            <span className="membership-benefits__badge" style={{ color: "#22C55E", borderColor: "#22C55E55", background: "#22C55E14" }}>Free membership</span>
          </div>
          <ul className="membership-benefits__list">
            {FEATURES.map(feature => (
              <li key={feature.label} className="membership-benefits__row">
                <Check size={17} strokeWidth={2.5} style={{ color: "#22C55E", flexShrink: 0 }} aria-hidden="true" />
                <span>{feature.label}</span>
              </li>
            ))}
          </ul>
        </section>
        <aside className="membership-purchase chi-showcase-surface">
          <div className="membership-purchase__identity">
            <div className="membership-purchase__icon" style={{ background: "#22C55E1F" }}>
              <Users size={20} strokeWidth={2.25} style={{ color: "#22C55E" }} aria-hidden="true" />
            </div>
            <div>
              <p style={{ color: "white", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: ".95rem", margin: 0 }}>CHI Alliance Membership</p>
              <p style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: ".72rem", margin: ".15rem 0 0" }}>Always free · No card required</p>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "1.35rem" }}>
            <span style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: ".84rem" }}>Due today</span>
            <strong style={{ color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "1.4rem" }}>$0.00</strong>
          </div>
          {isLoggedIn ? (
            <>
              <button
                type="button"
                onClick={() => activate.mutate()}
                disabled={activate.isPending}
                className="chi-btn-primary membership-checkout__button"
              >
                {activate.isPending ? <><Loader2 size={17} className="animate-spin" /> Activating membership…</> : <><Users size={17} /> Activate free membership</>}
              </button>
              {activate.error && (
                <p role="alert" style={{ margin: ".65rem 0 0", color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>{activate.error.message}</p>
              )}
              <p style={{ color: "#7F8BA0", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5, margin: ".65rem 0 0", textAlign: "center" }}>
                You already have an account. Activate free membership to open the full directory and member destinations.
              </p>
            </>
          ) : (
            <>
              <Link href="/create-account?source=membership" className="chi-btn-primary membership-checkout__button">
                <Users size={20} strokeWidth={2.25} aria-hidden="true" /> {MEMBERSHIP_JOIN_CTA}
              </Link>
              <p style={{ color: "#7F8BA0", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5, margin: ".65rem 0 0", textAlign: "center" }}>
                Create your profile in a few short steps. No card required.
              </p>
            </>
          )}
        </aside>
      </div>
    </>
  );
}

export default function PremiumUpgrade() {
  const { isLoggedIn, isPremium } = useAuth();
  const [, navigate] = useLocation();
  // Wouter's location hook is pathname-oriented. Stripe return parameters live
  // in the browser search string, so read it directly rather than silently
  // losing the success/cancel state on the membership route.
  const checkoutState = typeof window === "undefined"
    ? null
    : new URLSearchParams(window.location.search).get("checkout");

  // Membership is what this route advertises, so it opens on the paid tier; the
  // free tab is the alternative a visitor discovers, not the default they land on.
  const [plan, setPlan] = useState<PlanId>("member");
  const planDetails = PLANS[plan];

  return (
    <>
      <Navigation />
      <main className="membership-page">
        <div className="membership-page__backdrop" />
        <div className="membership-page__content">
          <EntitlementNotice checkoutState={checkoutState} />
          {isPremium ? (
            <section style={{ textAlign: "center", padding: "4rem 1rem" }}>
              <div style={{ width: 72, height: 72, borderRadius: "50%", margin: "0 auto 1.5rem", background: "rgba(233,30,140,.1)", border: "2px solid rgba(233,30,140,.3)", display: "grid", placeItems: "center" }}><CheckCircle2 size={30} style={{ color: "#F9A8D4" }} /></div>
              <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "2.5rem", color: "white", margin: "0 0 .75rem" }}>Membership Active</h1>
              <p style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", maxWidth: 500, margin: "0 auto 2rem", lineHeight: 1.65 }}>Your verified membership gives you access to the member directory and other eligible features.</p>
              <button type="button" onClick={() => navigate("/member-directory")} className="chi-btn-primary" style={{ padding: ".9rem 1.4rem" }}><Users size={16} /> Open Member Directory</button>
            </section>
          ) : !MEMBERSHIP_PAID_TIERS_ENABLED ? (
            <FreeMembershipJoin />
          ) : (
            <>
              <header className="membership-page__hero">
                <div style={{ display: "inline-flex", alignItems: "center", gap: ".5rem", padding: ".35rem 1rem", background: "rgba(233,30,140,.08)", border: "1px solid rgba(233,30,140,.2)", borderRadius: 999, marginBottom: "1.25rem" }}><Crown size={13} style={{ color: "#F9A8D4", flexShrink: 0 }} /><span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: ".7rem", fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: "#F9A8D4" }}>Create an Account</span></div>
                <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2.5rem, 7vw, 4.2rem)", color: "white", margin: "0 0 1rem", lineHeight: 1 }}>Create Your CHI Alliance Account</h1>
                <p style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body)", lineHeight: 1.7, margin: 0 }}>Two account types: a <strong style={{ color: "#86EFAC" }}>free</strong> profile, or full Membership at <strong style={{ color: "#F9A8D4" }}>$199 per year</strong> with billing securely managed by Stripe.</p>
              </header>
              <div className="membership-page__layout">
                <MembershipBenefits plan={plan} onChange={setPlan} />
                <aside className="membership-purchase chi-showcase-surface">
                  <PlanTabs plan={plan} onChange={setPlan} />
                  <div className="membership-purchase__identity"><div className="membership-purchase__icon" style={{ background: `${planDetails.accent}1F` }}>{plan === "free" ? <Users size={20} strokeWidth={2.25} style={{ color: planDetails.accent }} aria-hidden="true" /> : <Crown size={20} strokeWidth={2.25} style={{ color: planDetails.accent }} aria-hidden="true" />}</div><div><p style={{ color: "white", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: ".95rem", margin: 0 }}>{planDetails.name}</p><p style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: ".72rem", margin: ".15rem 0 0" }}>{planDetails.cadence}</p></div></div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "1.35rem" }}><span style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: ".84rem" }}>Due today</span><strong style={{ color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "1.4rem" }}>{planDetails.price}</strong></div>
                  {!isLoggedIn ? (
                    <MembershipAuthChoice plan={plan} />
                  ) : plan === "member" ? (
                    <MembershipCheckoutButton />
                  ) : (
                    /* Signed in and looking at the free tier: this is the account
                       they already have, so offer the thing it unlocks rather than
                       a create-account button that would do nothing. */
                    <>
                      <Link href="/member-directory" className="chi-btn-primary membership-checkout__button">
                        <Users size={20} strokeWidth={2.25} aria-hidden="true" /> Open Member Directory
                      </Link>
                      <p style={{ color: "#7F8BA0", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5, margin: ".65rem 0 0", textAlign: "center" }}>
                        You already have a free account. Switch to Member · Yearly to add the rest.
                      </p>
                    </>
                  )}
                </aside>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
